/* eslint-disable react/jsx-no-bind */
/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {ClickAwayListener, Paper} from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import moment from 'moment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Group} from '@ion/core/src/types/entities';
import {isArrayEmpty} from '@ion/core/src/utils/ArrayUtil';
import {
  safeJSONParse,
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  isDownArrow,
  isEnter,
  isTab
} from '@ion/core/src/utils/KeyEventUtils';
import {onlyMultipleNumbers, onlyNumbers} from '@ion/core/src/utils/NumberUtil';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import {TopNavSearchIcon} from '@ion/dls/src/icons/TopNavSearchIcon';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {FilterChips} from '@ion/dls/src/types/editors';
import {DLSTheme, ViewSubGroupsPosition} from '@ion/dls/src/types/types';
import GroupInheritance from './groups/GroupInheritance';
import GroupSearchInput from './groups/GroupSearchInput';
import AttributeSuggestions from './AttributeSuggestions';
import BaseSearchMapping from './BaseSearchMappings';
import DisplayQueryChips from './DisplayQueryChips';
import {SavedQueryItem, SavedQueryType} from './SaveQueriesList';
import SearchFieldTooltip from './SearchFieldTooltip';
import SearchScope, {
  hasValidSelection,
  isValidSelection,
  SearchScopeValues
} from './SearchScope';
import {SearchSuggestions} from './SearchSuggestions';
import {
  AccountIdLinkMap,
  AttributeTypes,
  copyPasteQueryNames,
  DateAttributes,
  DateLabels,
  Dropdown,
  LookUpData,
  LookUpSource,
  MenuItem,
  QueryChipData,
  SAVED_QUERIES_VALUE,
  SearchQuery,
  SearchScopeMatch,
  SuggestionsType
} from './types';
import {
  filterDynamicDataList,
  getDateSelection,
  getSelectedChipValue,
  IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT,
  isAnySuggestionType,
  isIdentifierLimitExceed,
  isSearchQueryValueType,
  isValidLogicalSearch,
  MAX_IDENTIFIER_SEARCH_LIMIT,
  PRIMARY_ID_SEARCH_LIMIT,
  removeEndSeperators
} from './utils';

export enum SuggestionOptions {
  GROUP = 'groupIds'
}

enum PageName {
  MFG_DEVICES = 'manufacturedDevices'
}

const debouncedInputSelectionMap = {
  [SuggestionOptions.GROUP] : true
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    widgetWrapper : {
      display                        : 'flex',
      flexDirection                  : 'column',
      gap                            : '10px',
      height                         : '100px',
      width                          : '60%',
      [theme.breakpoints.down('sm')] : {
        flexDirection  : 'row',
        alignItems     : 'center',
        justifyContent : 'space-between',
        height         : 'auto',
        width          : '100%',
        gap            : '25px',
        position       : 'relative',
        bottom         : '28px',
        padding        : '0 23px'
      }
    },
    widgetWrapperFullWidth : {
      display                        : 'flex',
      flexDirection                  : 'column',
      gap                            : '10px',
      height                         : '100px',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        height        : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 'auto' : '100px',
        flexDirection : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 'row' : 'column'
      }
    },
    searchWrapper : {
      display       : 'flex',
      flexDirection : 'row',
      flexWrap      : 'nowrap',
      gap           : 12
    },
    phoneSearchWrapper : {
      position : 'relative',
      width    : '75%'
    },
    searchSuggestionInputWrapper : {
      maxWidth : ({isPhone, isMobileModularSearch}) => (isPhone && isMobileModularSearch) ? '600px' : '500px',
      width    : ({isPhone, isMobileModularSearch}) => (isPhone && isMobileModularSearch) ? '100%' : '60%',
      position : 'relative'
    },
    searchSuggestionInputWrapperFullWidth : {
      width                          : '73%',
      position                       : 'relative',
      [theme.breakpoints.down('sm')] : {
        width : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? '100%' : '73%'
      }
    },
    searchSuggestionInputRoot : {
      height             : '48px',
      '& .MuiInput-root' : {
        minHeight : '48px'
      }
    },
    searchSuggestionInputUnderline : {
      padding     : '12px !important',
      height      : 'inherit',
      '&::before' : {
        borderBottom : 'transparent'
      },
      '&:hover' : {
        '&::before' : {
          borderBottom : `2px solid ${theme.dls.colors.aaBlue} !important`
        }
      }
    },
    suggestionsWrapper : {
      zIndex    : 5,
      top       : '-112px !important',
      transform : 'translate3d(0px, 160px, 0px) !important',
      left      : 'unset !important',
      right     : 'unset !important',
      border    : '1px solid #e0e0e0',
      maxWidth  : 500,
      width     : '100%'
    },
    searchIcon : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    copyPasteIcon : {
      '& span' : {
        display        : 'flex',
        justifyContent : 'center',
        alignItems     : 'center',
        cursor         : 'pointer'
      }
    },
    selectFormControl : {
      width                  : '300px',
      height                 : '48px',
      '& .MuiInputBase-root' : {
        width     : '100%',
        height    : '48px',
        marginTop : 0
      }
    },
    attributesSelect : {
      width      : '30%',
      minWidth   : 150,
      height     : '27px',
      display    : 'flex',
      alignItems : 'center',
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    },
    attributesSelectionMenu : {
      width     : '260px',
      maxHeight : 400,
      overflow  : 'scroll',
      overflowX : 'hidden',
      marginTop : 10,
      fontSize  : 14
    },
    attributesMenuItem : {
      height     : '48px',
      marginLeft : '11px',
      fontSize   : 14,
      color      : dlsColor('grey3')
    },
    input : {
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    }
  };
});

export interface ModularSearchWidgetProps {
  pageName: string;
  searchInstance?: BaseSearchMapping;
  getSearchParams?: Function;
  getIdentifierParams?: (param: string) => void;
  inputPlaceholder?: string;
  getFilterQueries?: (filters: FilterChips) => void;
  autoSelectNext?: boolean;
  showFullWidthSearch?: boolean;
  isPhone?: boolean;
  isMobileModularSearch?: boolean;
  isCaseInsensitive?: boolean;
  hasDateField?: boolean;
  accountTree: AccountIdLinkMap;
  accountId?: number;
  viewSubGroups?: boolean;
  viewInheritableGroup?: boolean;
  toggleInheritableGroupView?: () => void
  hasInheritableGroups?: boolean;
  isGridInsideADialog?: boolean;
}

/**
 * Constants for the component
 */
export const PREFERENCE_NAME = 'modularSearchQueries';
export const SEARCH_NAME     = 'last search';

const SEARCH_INPUT_ID    = 'search-input';
const SEARCH_WRAPPER     = 'modular-search-wrapper';
const VBUS_STATUS        = 'vbusEnabledStatus';
const IS_THIRD_PARTY     = 'isThirdParty';
const TRUE               = 'true';
const FALSE              = 'false';
const VBUS_STATUS_ON     = 'On';
const VBUS_STATUS_OFF    = 'Off';
const IS_THIRD_PARTY_YES = 'YES';
const IS_THIRD_PARTY_NO  = 'NO';
const INITIAL_SEARCH_QUERY = {
  query  : '',
  option : null
};

const updateFilteredExactMatch = (attribute, pageName, hasSavedQuery = null) => {
  let hasIndentifier         = false;
  const latestQueriesForPage = getSavedQueriesForPage(pageName);
  const matchingQuery        = latestQueriesForPage?.query?.find(({name}) => name === attribute);
  const {query}              = matchingQuery || {};
  if (!isNil(hasSavedQuery)) {
    latestQueriesForPage?.query?.forEach(({name, query}) => {
      if (isEqual(name, hasSavedQuery)) {
        const updatedQuery = safeJSONParse(query);
        hasIndentifier     = updatedQuery?.[0]?.hasConfig || updatedQuery?.[0]?.hasFirmware;
      }
    });
    return {
      hasQuery : hasIndentifier
    };
  } else if (matchingQuery) {
    return {
      hasQuery : query === SearchScopeValues.EXACT
    };
  }
};

export const hasFilteredExactMatch = ({
  queries,
  pageName,
  queryAttribute = null,
  hasSavedQuery = null,
  isEnter = false
}): SearchScopeMatch => {
  let isConfigVersion   = false;
  let isFirmwareVersion = false;
  if (isEnter) {
    if (hasValidSelection(queryAttribute)) {
      const {hasQuery} = updateFilteredExactMatch(queryAttribute, pageName) || {};
      if (isEqual(queryAttribute, SearchScopeValues.CONFIG_VERSION)) {
        isConfigVersion   = hasQuery;
      } else if (isEqual(queryAttribute, SearchScopeValues.FIRMWARE_VERSION)) {
        isFirmwareVersion = hasQuery;
      }
    }
  } else {
    queries?.forEach(({attribute}) => {
      const {hasQuery} = updateFilteredExactMatch(attribute, pageName, hasSavedQuery) || {};
      if (isEqual(attribute, SearchScopeValues.CONFIG_VERSION)) {
        isConfigVersion   = hasQuery;
      } else if (isEqual(attribute, SearchScopeValues.FIRMWARE_VERSION)) {
        isFirmwareVersion = hasQuery;
      }
    });
  }
  return {
    isConfigVersion,
    isFirmwareVersion
  };
};

export const getSavedQueriesForPage = (pageName) => {
  const savedQueries = UserPreferences.get(PREFERENCE_NAME) ?? [];
  const savedQueriesForPage: SavedQueryType = !isArrayEmpty(savedQueries)
    ? savedQueries.filter(({pageName: name}: SavedQueryType) => name === pageName)[0] ?? {}
    : {};
  return savedQueriesForPage;
};

export const removeTempQueries = (pageName: string) => {
  const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
  const savedQueriesLength = savedQueriesForPage?.query?.length;
  if (savedQueriesLength) {
    const filteredQueries = savedQueriesForPage?.query?.filter(({name}: SavedQueryItem) => name !== SEARCH_NAME);
    return {
      ...savedQueriesForPage,
      query : cloneDeep(filteredQueries)
    };
  }
  return savedQueriesForPage;
};

export const isEqualArrays = (array1: string[], array2: string[]) => {
  const array2Sorted = array2.slice().sort();
  return array1.length === array2.length && array1.slice().sort().every(function(value, index) {
    return value === array2Sorted[index];
  });
};

/**
 * It will display GlobalSearchWidget, here user can enter keyword
 * to search data in Accounts/Users/Assets/Devices/Groups
 */
export const ModularSearchWidget = ({
  getFilterQueries,
  pageName,
  searchInstance,
  getSearchParams,
  getIdentifierParams,
  toggleInheritableGroupView,
  inputPlaceholder,
  autoSelectNext,
  isPhone = false,
  viewInheritableGroup = false,
  isMobileModularSearch = false,
  hasInheritableGroups = false,
  showFullWidthSearch = false,
  isCaseInsensitive = false,
  hasDateField = false,
  viewSubGroups = false,
  isGridInsideADialog = false,
  accountTree = {},
  accountId
}: ModularSearchWidgetProps) => {
  const classes                                         = useStyles(
    {isPhone, isMobileModularSearch, isGridInsideADialog});
  const queryInputRef                                   = useRef(null);
  const menuListRef                                     = useRef(null);
  const hasSavedQuery                                   = useRef(null);
  const isMounted                                       = useHasMounted();
  const latestQueriesForPage                            = getSavedQueriesForPage(pageName);
  const [queryAttribute, setQueryAttribute]             = useState<string>(searchInstance.getFirstAttributeLabel());
  const [searchQuery, updateSearchQuery]                = useState<SearchQuery>(INITIAL_SEARCH_QUERY);
  const [queries, updateQueries]                        = useState<QueryChipData[]>([]);
  const [menuAutoFocus, updateMenuAutoFocus]            = useState<boolean>(false);
  const [savedQueryName, setSavedQueryName]             = useState<string | null>(
    latestQueriesForPage?.queryName !== SEARCH_NAME ? latestQueriesForPage?.queryName : null);
  const [isFiltersSaved, setIsFiltersSaved]             = useState<boolean>(false);
  const [isSearchError, setIsSearchError]               = useState<boolean>(false);
  const [isSaveRequired, setIsSaveRequired]             = useState<boolean>(false);
  const [canShowDeletePrompt, onCanShowDeletePrompt]    = useState<string | null>(null);
  const [filterLookUpData, setFilterLookUpData]         = useState<LookUpData[]>(null);
  const [showSuggestions, setShowSuggestions]           = useState(false);
  const [suggestionDropDown, updateSuggestionsDropdown] = useState<Dropdown>({
    open       : null,
    type       : SuggestionsType.TEXT,
    sourceType : null
  });

  const searchInputPlaceholder = `${inputPlaceholder} by ${searchInstance.getLabel(queryAttribute)}`;

  const mappings = searchInstance.mappings[queryAttribute];
  const limit    = mappings?.hasLargeMaxLimit ? IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT
    : mappings?.hasIDMaxLimit ? PRIMARY_ID_SEARCH_LIMIT : MAX_IDENTIFIER_SEARCH_LIMIT;
  const defaultSearchErrMsg = translate('common:search.globalSearchLimitError', {limit});
  const [shouldSaveQueryOnEnter, setShouldSaveQueryOnEnter] = useState(false);
  const [searchErrMsg, setSearchErrMsg]                     = useState<string>(defaultSearchErrMsg);

  const isGroupSuggestion = suggestionDropDown?.sourceType === LookUpSource.Group;

  /**
   * Function to open suggestions dropdown
   * @private
   */
  function setSuggestionOpen(element, type, sourceType) {
    updateSuggestionsDropdown({open: element, type, sourceType});
  }

  /**
   * @private
   * Utility function to remove all queries saved in user preference.
   */
  async function resetSavedQueriesForPage() {
    const userPreferences = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const otherPages: SavedQueryType[] =
      userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
    await UserPreferences.set(PREFERENCE_NAME, [...otherPages], !UserPreferences.hasChangeDetection());
  }

  async function removeEmptyQueries() {
    const userPreferences = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    const savedQueriesLength = savedQueriesForPage?.query?.length;
    if (savedQueriesLength) {
      const filteredQueriesForPage = savedQueriesForPage.query?.filter(
        ({query}: SavedQueryItem) => query && (safeJSONParse(query)?.length || query?.length));
      const filteredQueriesLength = filteredQueriesForPage?.length;
      if (savedQueriesLength > filteredQueriesLength) {
        const otherPageQueries = userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
        if (filteredQueriesLength) {
          savedQueriesForPage.query = filteredQueriesForPage;
          await UserPreferences.set(
            PREFERENCE_NAME,
            [...otherPageQueries, savedQueriesForPage],
            !UserPreferences.hasChangeDetection()
          );
        } else {
          await UserPreferences.set(
            PREFERENCE_NAME,
            [...otherPageQueries],
            !UserPreferences.hasChangeDetection()
          );
        }
      }
    }
  }

  const getDefaultQuery = useCallback(() => {
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    if (!isEmpty(savedQueriesForPage)) {
      const selectedQuery = savedQueriesForPage?.query?.filter(
        ({name}: SavedQueryItem) => name === savedQueriesForPage?.queryName)?.[0] ?? {};
      return selectedQuery as SavedQueryItem;
    }
    return {} as SavedQueryItem;
  }, [pageName]);

  const hasChangeInSearch = useCallback(() => {
    if (queries?.length) {
      if (savedQueryName) {
        const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
        const savedQuery = savedQueriesForPage.query.filter(({name}) => name === savedQueryName)?.[0];
        return safeJSONStringify(queries) !== savedQuery?.query;
      }
      return true;
    }
    return false;
  }, [pageName, queries, savedQueryName]);
  
  useEffect(() => {
    if (!isNull(queries) && shouldSaveQueryOnEnter) {
      if (hasChangeInSearch()) {
        saveQuery(SEARCH_NAME, undefined, false);
      }
      setShouldSaveQueryOnEnter(false);
    }
  }, [queries, shouldSaveQueryOnEnter]);

  // using default search from UserPreferences if exists
  useEffect(() => {
    removeEmptyQueries();
    const defaultQuery = getDefaultQuery();
    if (!isEmpty(defaultQuery)) {
      onSavedQuerySelect(defaultQuery);
    } else {
      updateQueries([]);
    }
  }, []);

  //setting default query for Manufactured Devices
  useEffect(() => {
    const lastOneMonth = moment().subtract(1, 'months').format('MM-DD-YYYY');
    if ((pageName === PageName.MFG_DEVICES) && (queries?.length === 0)) {
      updateQueries([{
        label     : translate('common:modularsearch.date.lastOneMonth'),
        attribute : 'createdOn',
        value     : lastOneMonth
      }]);
    }
  }, [queries, pageName]);

  // when change in saved query name, save in to User Preferences
  useEffect(() => {
    const saveDefaultQueryName = async() => {
      const userPreferences = UserPreferences.get(PREFERENCE_NAME) ?? [];
      const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
      if (!isEmpty(savedQueriesForPage) && (savedQueriesForPage.queryName !== savedQueryName || !queries?.length)) {
        if (queries?.length) {
          savedQueriesForPage.queryName = savedQueryName;
        }
        const otherPageQueries = userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
        await UserPreferences.set(
          PREFERENCE_NAME,
          [...otherPageQueries, savedQueriesForPage],
          !UserPreferences.hasChangeDetection()
        );
      }
    };

    return () => {
      if (!hasChangeInSearch()) {
        saveDefaultQueryName();
      }
    };
  }, [pageName, queries, savedQueryName]);

  // when change in search, save in to User Preferences
  useEffect(() => {
    if (isSaveRequired) {
      saveQuery(savedQueryName ?? SEARCH_NAME, undefined, false);
    }
  }, [pageName, queries, savedQueryName, isSaveRequired]);

  useEffect(function() {
    if (isFiltersSaved) {
      const timeoutRef = setTimeout(function() {
        setIsFiltersSaved(false);
        clearTimeout(timeoutRef);
      }, 3000);
    }
  }, [isFiltersSaved]);

  useEffect(function() {
    if (isSaveRequired) {
      const timeoutVal = setTimeout(function() {
        setIsSaveRequired(false);
        clearTimeout(timeoutVal);
      }, 3000);
    }
  }, [isSaveRequired]);

  useEffect(() => {
    if (isMounted) {
      const hasExactMatch = hasFilteredExactMatch({
        queries,
        pageName,
        hasSavedQuery : hasSavedQuery.current
      });
      getSearchParams(searchInstance.transformQueriesToRequestParams(queries, hasExactMatch));
      getIdentifierParams?.(hasSavedQuery.current);
      if (queryAttribute === SAVED_QUERIES_VALUE) {
        const firstAvailableQuery = searchInstance.getFirstAttributeLabel(queries, autoSelectNext);
        setQueryAttribute(firstAvailableQuery);
      }
    }
    getFilterQueries?.(queries);
  }, [queries, pageName]);

  useEffect(() => {
    if (queryAttribute) {
      if (queryAttribute !== SAVED_QUERIES_VALUE) {
        const {
          type
        } = searchInstance.getMappingObject(queryAttribute);
        if (type === AttributeTypes.TEXT) {
          setSuggestionOpen(false, null, null);
        }
      } else {
        setSuggestionOpen(false, null, null);
      }
    }
  }, [queryAttribute]);

  /**
   * Function to cleanup suggestion dropdown
   * @private
   */
  const handleClose = useCallback(function() {
    updateSuggestionsDropdown({
      open       : suggestionDropDown.type === SuggestionsType.DATE,
      type       : suggestionDropDown.type,
      sourceType : suggestionDropDown.sourceType
    });
    if (!isEqual(queryAttribute, 'progressStatus')) {
      updateSearchQuery(INITIAL_SEARCH_QUERY);
    }
    if (suggestionDropDown.type !== SuggestionsType.DATE) {
      setShowSuggestions(false);
    }
  }, [suggestionDropDown, setShowSuggestions, queryAttribute]);

  const onSelectAttribute = useCallback(function(event, fromSource = false) {
    const {target: {value, dataset}} = event;
    const item = (value === 0 ? dataset?.value : value) || queryAttribute;
    setQueryAttribute(item);
    if (item !== 'saved') {
      updateSearchQuery(INITIAL_SEARCH_QUERY);
      const timeoutRef = setTimeout(function() {
        fromSource && onKeyDown(new KeyboardEvent('keypress', {key: 'ArrowDown', which: 13}), item);
        queryInputRef?.current?.focus();
        clearTimeout(timeoutRef);
      }, 300);
    }
  }, [queryInputRef, suggestionDropDown]);

  /**
   * Function to track input element and dropdown keydown
   * @private
   */
  const onKeyDown = useCallback(function(event, attribute = null) {
    event.stopPropagation();
    if (!isSearchError) {
      if (isDownArrow(event)) {
        event.preventDefault();
        const attributeForQuery = attribute ?? queryAttribute;
        const mapping = searchInstance.getMappingObject(attributeForQuery);
        if (attributeForQuery?.trim() !== SAVED_QUERIES_VALUE && mapping.type !== AttributeTypes.TEXT) {
          updateSuggestionsDropdown({
            type       : mapping?.suggestion,
            sourceType : mapping?.suggestionsSource,
            open       : true
          });
          if (event?.target?.id === SEARCH_INPUT_ID) {
            menuListRef.current?.children[0]?.focus();
          }
        }
      } else if (isEnter(event) && !isGroupSuggestion) {
        let record = null;
        if (isAnySuggestionType(SuggestionsType.LOOKUP)(suggestionDropDown.type) && !searchQuery.option) {
          record = filterDynamicDataList(filterLookUpData, searchQuery?.query, -1, 'name', isCaseInsensitive);
          if (record.length === 1) {
            searchQuery.option = record[0];
          }
          record = record.length > 1 ? null : record;
        } else {
          record = searchQuery;
        }

        if (record) {
          onEnter(searchQuery);
          updateSuggestionsDropdown({open: null, type: suggestionDropDown.type, sourceType: null});
        }
      } else if (isTab(event)) {
        updateSuggestionsDropdown({open: null, type: suggestionDropDown.type, sourceType: null});
      }
    } else {
      setIsSearchError(false);
    }
  }, [queries, searchQuery, suggestionDropDown, queryAttribute]);

  // Submits the given search query as a chip.
  const onEnter = useCallback(function(searchQuery: SearchQuery) {
    if (searchQuery?.query?.length) {
      if (queryAttribute?.toLowerCase() === 'any' && !isValidLogicalSearch(searchQuery)) {
        setIsSearchError(true);
        setSearchErrMsg('Invalid Logical search provided.');
        return false;
      }
      const compactSearchQuery = removeEndSeperators(searchQuery?.query, isValidOperation(queryAttribute));
      const queriesData = searchInstance
        .createQueryChips({
          attribute   : queryAttribute,
          queryOption : {
            ...searchQuery,
            query : compactSearchQuery
          }
        });
      const doesQueryExist = queries?.findIndex((query: QueryChipData) => query.attribute === queriesData.attribute);
      if (queriesData) {
        let combinedQuery = [...queries];
        if (doesQueryExist === -1) {
          combinedQuery = [...combinedQuery, queriesData];
        } else {
          combinedQuery[doesQueryExist] = queriesData;
        }
        const isExactSearch = hasFilteredExactMatch({
          queries : latestQueriesForPage,
          isEnter : true,
          pageName,
          queryAttribute
        });
        if (isEqual(queryAttribute, 'progressStatus')) {
          getSearchParams(searchInstance.transformQueriesToRequestParams(combinedQuery));
        } if (hasValidSelection(queryAttribute) && isExactSearch) {
          getSearchParams(searchInstance.transformQueriesToRequestParams(combinedQuery, isExactSearch));
          const {
            isConfigVersion,
            isFirmwareVersion
          } = isExactSearch || {};
          combinedQuery?.forEach(query => {
            const {attribute} = query || {};
            if (isEqual(SearchScopeValues.CONFIG_VERSION, attribute) && isConfigVersion) {
              query.hasConfig   = isConfigVersion;
            } if (isEqual(SearchScopeValues.FIRMWARE_VERSION, attribute) && isFirmwareVersion) {
              query.hasFirmware = isFirmwareVersion;
            }
          });
        }
        updateQueries(combinedQuery);
        updateMenuAutoFocus(false);
        if (!isEqual(queryAttribute, 'progressStatus')) {
          updateSearchQuery(INITIAL_SEARCH_QUERY);
          setSuggestionOpen(false, queryInputRef.current, null);
          if (autoSelectNext) {
            const firstQuery = searchInstance.getFirstAttributeLabel(combinedQuery, autoSelectNext);
            setQueryAttribute(firstQuery);
          }
        }
        if (!isNull(combinedQuery)) {
          setShouldSaveQueryOnEnter(true);
        }
      }
    }
    setIsSearchError(false);
  }, [queryAttribute, searchQuery, queries, savedQueryName, latestQueriesForPage]);

  /**
   * When user selects an options from multi-select and presses tab,
   * it will trigger onKeyDown event with Enter key.
   */
  const inputEventTrigger = useCallback(function(shouldSelect: boolean) {
    queryInputRef?.current.focus();
    if (shouldSelect) {
      onKeyDown(new KeyboardEvent('keypress', {key: 'Enter', which: 13}));
    }
  }, [queries, searchQuery, queryAttribute]);

  const onInputChange = useCallback(({target: {value}}: ChangeEvent<HTMLInputElement>) => {
    const mapping = searchInstance?.mappings[queryAttribute];
    setIsSearchError(isIdentifierLimitExceed({
      queryValue       : value,
      hasLargeMaxLimit : mapping?.hasLargeMaxLimit,
      hasIDMaxLimit    : mapping?.hasIDMaxLimit,
      hasAnyField      : mapping?.hasAnyField
    }));
    setSearchErrMsg(defaultSearchErrMsg);
    updateSearchQuery({
      query  : mapping.hasNumericValue ? ((mapping.hasMultipleNumericValue ? onlyMultipleNumbers(value) : onlyNumbers(value)) || '').toString() : value,
      option : null
    });
    if (debouncedInputSelectionMap[mapping?.key]) {
      debouncedHandleInput(value, mapping);
    } else {
      setShowSuggestions(true);
      if (isAnySuggestionType(SuggestionsType.LOOKUP)(mapping.suggestion)) {
        onKeyDown(new KeyboardEvent('keypress', {key: 'ArrowDown', which: 13}), queryAttribute);
      } else if (!isAnySuggestionType(SuggestionsType.TEXT)(mapping.suggestion)) {
        setSuggestionOpen(true, mapping.suggestion, suggestionDropDown.sourceType);
      }
    }
  }, [queryAttribute, updateSearchQuery, setIsSearchError, searchInstance]);

  const debouncedHandleInput = useCallback(debounce((value: string, mapping) => {
    if (value?.length < 1) {
      setShowSuggestions(false);
    }
    if (value?.length >= 2) {
      setShowSuggestions(true);
      updateSuggestionsDropdown({
        open       : true,
        type       : mapping.suggestion,
        sourceType : mapping.suggestionsSource
      });
    }
  }, 500), [setShowSuggestions]);

  /**
   * @param menuItem contains SEARCH_LABEL SEARCH_CONDITION. e.g: NAME CONTAINS
   * @param source specifies where the selection is from
   */
  const onMenuSelection = useCallback(function(
    menuItem: MenuItem,
    source: SuggestionsType,
    event?: KeyboardEvent | {canSubmit: boolean}
  ) {
    const selection = searchInstance.getSelectedQuery({
      menuItem,
      source,
      attribute : queryAttribute,
      query     : searchQuery.query
    });
    if (isSearchQueryValueType(menuItem)) {
      menuItem.displayTitle =
        searchInstance.getDisplayableQueryAttribute(queryAttribute, menuItem);
    }
    const queryToUpdate = {
      query  : selection,
      option : isAnySuggestionType(SuggestionsType.LOOKUP, SuggestionsType.DATE)(source) ? menuItem : null
    };
    updateSearchQuery(queryToUpdate);

    if ((BaseSearchMapping.isMultiSelectionType && event?.type === 'click') && selection) {
      onEnter(queryToUpdate);
    }

    if ((BaseSearchMapping.isSelectionType(source) ||
      (BaseSearchMapping.isDateSelection(source) && event?.canSubmit)) && selection) {
      onEnter(queryToUpdate);
    }
  }, [searchQuery, queryAttribute]);

  /**
   * Function to update state lookup data.
   * @private
   */
  const updateFilterLookUpData = useCallback((data) => {
    setFilterLookUpData(data);
  }, []);

  /**
   * Function to remove the query chip.
   * @private
   */
  const onQueryRemove = useCallback(function(attribute: string) {
    return function() {
      const filteredQueries = queries.filter((queryChip: QueryChipData) => queryChip.attribute !== attribute);
      updateQueries([...filteredQueries]);
      setQueryAttribute(searchInstance.getFirstAttributeLabel(filteredQueries, autoSelectNext));
      updateSearchQuery(INITIAL_SEARCH_QUERY);
      if (isEmpty(filteredQueries)) {
        setSavedQueryName(null);
        setIsSaveRequired(true);
      }
    };
  }, [queries]);

  const saveQuery = useCallback(async function(savedName: string, callback, showMessage = true) {
    const name         = savedName?.length > 0 ? savedName : savedQueryName;
    const savedQueries = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    if (queries.length > 0) {
      if (isEmpty(savedQueriesForPage)) {
        savedQueriesForPage.pageName  = pageName;
        savedQueriesForPage.createdAt = new Date();
        savedQueriesForPage.query     = [];
        savedQueriesForPage.queryName = '';
      } else if (name) {
        savedQueriesForPage.queryName = name;
      }
      if (!showMessage) {
        savedQueriesForPage.queryName = name;
      }
      const savedQueryIndex = savedQueriesForPage
        .query
        .findIndex(({name: savedQueryName}) => name === savedQueryName);
      if (hasDateField) {
        queries?.forEach(query => {
          const {attribute, value} = query || {};
          if (DateAttributes.indexOf(attribute) !== -1 && DateLabels.indexOf(value?.label) !== -1) {
            if (Object?.getOwnPropertyDescriptor(query?.value, 'value')?.writable) {
              value.value = getDateSelection(value?.label);
            }
          }
        });
      }
      if (savedQueryIndex >= 0) {
        savedQueriesForPage.query[savedQueryIndex].query = safeJSONStringify(queries);
      } else {
        savedQueriesForPage.query.push({
          name,
          query : safeJSONStringify(queries)
        });
        callback?.();
      }
      const modularSavedQueries = savedQueries?.filter(
        (page: SavedQueryType) => page.pageName !== pageName);
      await UserPreferences.set(
        PREFERENCE_NAME,
        [...modularSavedQueries, savedQueriesForPage],
        !UserPreferences.hasChangeDetection()
      );
      if (name !== SEARCH_NAME) {
        setSavedQueryName(name);
      }
      if (showMessage) {
        setIsFiltersSaved(true);
      }
    } else if (queries.length === 0 && savedQueriesForPage.queryName) {
      savedQueriesForPage.queryName = null;
      const modularSavedQueries = savedQueries?.filter(
        (page: SavedQueryType) => page.pageName !== pageName);
      await UserPreferences.set(
        PREFERENCE_NAME,
        [...modularSavedQueries, savedQueriesForPage],
        !UserPreferences.hasChangeDetection()
      );
    }
  }, [queries, pageName]);

  const onSavedQuerySelect = useCallback(function({name, query}: SavedQueryItem) {
    if (name) {
      if (name !== SEARCH_NAME) {
        setSavedQueryName(name);
      }
      const selectedQuery = safeJSONParse(query);
      updateQueries(selectedQuery);
      setIsSaveRequired(true);
    }
    hasSavedQuery.current = name;
  }, []);

  const onConfirmRemove = useCallback(function(savedQueryName: string) {
    onCanShowDeletePrompt(savedQueryName);
  }, []);

  const onRemoveQuery = useCallback(async function(savedQueryName: string) {
    const savedQueries = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    if (!isEmpty(savedQueriesForPage)) {
      const updatedQueries = savedQueriesForPage.query.filter(({name}) => name !== savedQueryName);
      savedQueriesForPage.query = updatedQueries;
      await UserPreferences.set(
        PREFERENCE_NAME,
        [...savedQueries, savedQueriesForPage],
        !UserPreferences.hasChangeDetection()
      );
      setSavedQueryName(null);
      updateQueries([]);
      setIsSaveRequired(true);
    }
  }, [pageName]);

  const onCloseMessage = useCallback(function() {
    setIsFiltersSaved(false);
  }, []);

  const onClearAll = useCallback(function() {
    setSavedQueryName(null);
    updateQueries([]);
    setIsSaveRequired(true);
    updateSearchQuery(INITIAL_SEARCH_QUERY);
  }, [savedQueryName]);

  const onDeleteConfirm = useCallback(function() {
    onRemoveQuery(canShowDeletePrompt);
    onCanShowDeletePrompt(null);
  }, [canShowDeletePrompt]);

  const onDeleteCancel = useCallback(function() {
    onCanShowDeletePrompt(null);
  }, []);

  const deletePromptOverride = useCallback(() =>
    translate('common:modularsearch.deleteConfirmationMessage', {
      name : canShowDeletePrompt
    }), [canShowDeletePrompt]);

  const onUndo = useCallback(async function(queryName) {
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    const selectedQuery = savedQueriesForPage?.query?.filter(
      ({name}: SavedQueryItem) => name === queryName)?.[0];
    const parsedQuery = !isEmpty(selectedQuery) ? safeJSONParse(selectedQuery?.query) : [];
    updateQueries(parsedQuery);
    setIsSaveRequired(true);
  }, [pageName, queries]);

  const isValidOperation = (queryName): boolean => {
    if (isEqual(copyPasteQueryNames.ESN, queryName) || isEqual(copyPasteQueryNames.APPID, queryName) ||
    isEqual(copyPasteQueryNames.SERIALNUMBER, queryName) || isEqual(copyPasteQueryNames.ANY, queryName)) {
      if (isEqual(copyPasteQueryNames.DEVICES, pageName)) {
        return true;
      }
    }
    return false;
  };

  const getDynamicChipValue = useCallback((queryChipData, value) => {
    if (isEqual(queryChipData?.attribute, VBUS_STATUS)) {
      if (isEqual(value, FALSE)) {
        return VBUS_STATUS_OFF;
      } if (isEqual(value, TRUE)) {
        return VBUS_STATUS_ON;
      }
    } else if (isEqual(queryChipData?.attribute, IS_THIRD_PARTY)) {
      if (isEqual(value, FALSE)) {
        return IS_THIRD_PARTY_NO;
      } if (isEqual(value, TRUE)) {
        return IS_THIRD_PARTY_YES;
      }
    }
    return '';
  }, []);

  const getChipValue = (attribute: string, dynamicValue: string, value: string) => ({
    [VBUS_STATUS]    : dynamicValue,
    [IS_THIRD_PARTY] : dynamicValue
  }[attribute] ?? value);

  const onClickQueryChip = useCallback(function(queryChipData) {
    const {attribute, value} = queryChipData;
    const dynamicChipValue   = getDynamicChipValue(queryChipData, value);
    const chipValue          = getChipValue(queryChipData?.attribute, dynamicChipValue, value);
    const searchValue        = getSelectedChipValue(chipValue, attribute);
    const mapping            = searchInstance.getMappingObject(attribute);
    if (mapping) {
      updateSearchQuery(searchValue);
      setQueryAttribute(attribute);
      updateSuggestionsDropdown({
        open       : mapping.suggestion !== SuggestionsType.TEXT,
        type       : mapping.suggestion,
        sourceType : mapping.suggestionsSource
      });
    }
  }, []);

  if (isMounted) {
    return (
      <Hidden show={!isMobileModularSearch ? 'desktop' : ''}>
        <ClickAwayListener onClickAway={handleClose}>
          {searchInstance && (
            <div className={showFullWidthSearch ? classes.widgetWrapperFullWidth : classes.widgetWrapper}>
              <div className={!isPhone ? classes.searchWrapper : classes.phoneSearchWrapper}>
                <Hidden show='desktop'>
                  <AttributeSuggestions onSelection={function(e) {
                    onSelectAttribute(e, true);
                  }}
                  value={queryAttribute}
                  classes={classes}
                  mapping={searchInstance.mappings}
                  listOptions={searchInstance.getSearchableOptions(
                    searchQuery.query,
                    SuggestionsType.ATTRIBUTE,
                    queries
                  )} />
                </Hidden>
                <div id={SEARCH_WRAPPER}
                  className={showFullWidthSearch ? classes.searchSuggestionInputWrapperFullWidth
                    : classes.searchSuggestionInputWrapper}>
                  <ConditionalRender condition={isGroupSuggestion}>
                    <GroupSearchInput placeholder={searchInputPlaceholder}
                      onMenuSelection={onMenuSelection}
                      onQueryRemove={onQueryRemove}
                      viewSubGroups={viewSubGroups}
                      accountTree={accountTree}
                      accountId={accountId}
                      queries={queries} />
                  </ConditionalRender>
                  <ConditionalRender condition={!isGroupSuggestion}>
                    <TextField id={SEARCH_INPUT_ID}
                      classes={{root: classes.searchSuggestionInputRoot}}
                      autoComplete={'off'}
                      placeholder={searchInputPlaceholder}
                      helperText={isGroupSuggestion && translate('common:modularsearch.groupsHelperText')}
                      inputRef={queryInputRef}
                      onClick={onInputChange}
                      onChange={onInputChange}
                      onKeyDown={onKeyDown}
                      value={searchQuery.query}
                      hasError={isSearchError}
                      errorText={searchErrMsg}
                      aria-controls={open ? 'menu-list-grow' : undefined}
                      aria-haspopup="true"
                      inputProps={{
                        maxLength : searchInstance.getSelectionCharacterLength(queryAttribute)
                      }}
                      InputProps={{
                        classes : {
                          input     : classes.input,
                          underline : classes.searchSuggestionInputUnderline
                        },
                        startAdornment : (<div className={classes.searchIcon}>
                          <TopNavSearchIcon />
                        </div>),
                        endAdornment : (
                          <>
                            <div className={classes.copyPasteIcon}>
                              <ConditionalRender condition={isValidOperation(queryAttribute)}>
                                <SearchFieldTooltip queryName={queryAttribute}
                                  searchInstance={searchInstance}
                                  limit={limit} />
                              </ConditionalRender>
                            </div>
                            <div>
                              <ConditionalRender condition={isValidSelection(queryAttribute, pageName)}>
                                <SearchScope queryAttribute={queryAttribute}
                                  pageName={pageName} />
                              </ConditionalRender>
                            </div>
                          </>
                        )
                      }}/>
                  </ConditionalRender>
                  <Popper open={Boolean(suggestionDropDown.open)}
                    className={classes.suggestionsWrapper}
                    anchorEl={suggestionDropDown.open ? queryInputRef.current : null}
                    role={undefined}
                    transition
                    disablePortal
                    style={{}}>
                    <Paper>
                      {Boolean(suggestionDropDown.open) && showSuggestions && (
                        <SearchSuggestions menuListRef={menuListRef}
                          inputValue={searchQuery.query}
                          inputEventTrigger={inputEventTrigger}
                          closeDropdown={() => {}}
                          isCaseInsensitive={isCaseInsensitive}
                          hasDateField={hasDateField}
                          selectedSuggestions={searchInstance.getSelectedSuggestion(searchQuery.query)}
                          query={searchInstance.getValue(searchQuery.query)}
                          menuAutoFocus={menuAutoFocus}
                          onMenuSelection={onMenuSelection}
                          menuSelectionType={suggestionDropDown.type}
                          attributeMappings={searchInstance.mappings}
                          lookUp={!isNull(suggestionDropDown.sourceType)
                            ? searchInstance.getCurrentLookup(suggestionDropDown.sourceType)
                            : null
                          }
                          updateFilterLookUpData={updateFilterLookUpData}
                          listOptions={searchInstance.getSearchableOptions(
                            queryAttribute,
                            suggestionDropDown.type,
                            queries
                          )}
                          accountTree={accountTree}
                          setShowSuggestions={setShowSuggestions} />
                      )}
                    </Paper>
                  </Popper>
                </div>
              </div>
              <DisplayQueryChips queries={queries}
                isPhone={isPhone}
                isGridInsideADialog={isGridInsideADialog}
                pageName={pageName}
                onUndo={onUndo}
                onClearAll={onClearAll}
                queryName={savedQueryName}
                onQueryRemove={onQueryRemove}
                saveQuery={saveQuery}
                adornmentClass={classes.searchIcon}
                onClickQueryChip={onClickQueryChip}
                onRemoveQuery={onConfirmRemove}
                onSavedQuerySelect={onSavedQuerySelect}
                showFullWidthSearch={showFullWidthSearch} />
            </div>
          )}
        </ClickAwayListener>
        <ConditionalRender condition={isGroupSuggestion && hasInheritableGroups}>
          <GroupInheritance checked={viewInheritableGroup}
            onChange={toggleInheritableGroupView}
            position={ViewSubGroupsPosition.GRID_CTAS} />
        </ConditionalRender>
        <IonSnackbar show={isFiltersSaved}
          message={translate('common:modularsearch.filtersSaved')}
          actionLabel={translate('common:editors.delete.ok')}
          onAction={onCloseMessage}
          onClose={onCloseMessage}
          autoHideDuration={3000} />
        {canShowDeletePrompt && (
          <DeletePrompt show
            entity={translate('common:modularsearch.savedSearch')}
            singleDeleteMessage={false}
            overrideMessage={deletePromptOverride}
            onConfirm={onDeleteConfirm}
            onCancel={onDeleteCancel}
            items={{}} />
        )}
      </Hidden>
    );
  }
  return null;
};

export default ModularSearchWidget;
